/**
 * Timezone-aware date helpers.
 *
 * These use Intl.DateTimeFormat to resolve date components in a specific IANA
 * timezone, avoiding the classic bug where `.getMonth()` returns the
 * browser-local or UTC month instead of the tenant's configured timezone.
 *
 * Default timezone is Australia/Sydney (AEST/AEDT).
 */

/**
 * Default timezone for all date calculations.
 * Used as fallback when no facility/tenant timezone is available.
 */
export const DEFAULT_TZ = 'Australia/Sydney';
export { resolveLocalDateTime, localDateTimeToUtcIso } from './local-datetime';
export type { LocalTimeCandidate, LocalTimeResolution, LocalTimeOptions } from './local-datetime';

/**
 * Returns the 0-based month (0=Jan, 11=Dec) for the given Date in the specified timezone.
 * Uses Intl.DateTimeFormat to correctly handle DST and UTC offset differences.
 */
export function getMonthInTz(date: Date, tz: string = DEFAULT_TZ): number {
  return (
    parseInt(
      new Intl.DateTimeFormat('en-US', { month: 'numeric', timeZone: tz }).format(date),
      10,
    ) - 1
  );
}

/**
 * Returns the full year for the given Date in the specified timezone.
 */
export function getYearInTz(date: Date, tz: string = DEFAULT_TZ): number {
  return parseInt(
    new Intl.DateTimeFormat('en-US', { year: 'numeric', timeZone: tz }).format(date),
    10,
  );
}

/**
 * Returns the day of month (1-31) for the given Date in the specified timezone.
 */
export function getDayInTz(date: Date, tz: string = DEFAULT_TZ): number {
  return parseInt(
    new Intl.DateTimeFormat('en-US', { day: 'numeric', timeZone: tz }).format(date),
    10,
  );
}

/**
 * Returns the hour of day (0–23) for the given Date in the specified timezone.
 *
 * Uses `hourCycle: 'h23'` so midnight is 0, not 24. Do not replace with
 * `date.getHours()` or ISO-string slicing — those read the runtime or UTC
 * clock, not the tenant's wall time.
 */
export function getHourInTz(date: Date, tz: string = DEFAULT_TZ): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(date);
  const hour = parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0', 10);
  // Some engines still surface "24" for midnight under 2-digit + locale combos.
  return hour === 24 ? 0 : hour;
}

/**
 * Returns the calendar date (YYYY-MM-DD) for the given Date in the specified timezone.
 *
 * Replacement for `.toISOString().slice(0, 10)` — that idiom always serialises
 * in UTC and returns yesterday's date between 00:00–10:00 AEST, which breaks
 * legal-date fields and consecutive-day / rostering / audit-log grouping.
 *
 * Uses Intl.DateTimeFormat.formatToParts so it works reliably on Hermes
 * (React Native / Android) where re-parsing toLocaleString output is unsafe.
 */
export function dateOnlyInTz(date: Date, tz: string = DEFAULT_TZ): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/**
 * Returns the start-of-day and end-of-day UTC instants for the tenant-local
 * calendar date `ymd` (a 'YYYY-MM-DD' string). Use for filters like
 * `recorded_at >= startUtc AND recorded_at <= endUtc` where the user picked
 * a date in their tenant's local calendar but the column is a UTC timestamp.
 *
 * Replaces the bug pattern `${ymd}T00:00:00Z` / `${ymd}T23:59:59Z`, which
 * builds UTC instants and offsets every Sydney query by 10–11 hours.
 */
export function dayBoundsInTz(
  ymd: string,
  tz: string = DEFAULT_TZ,
): { startUtc: string; endUtc: string } {
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) {
    throw new Error(`dayBoundsInTz: invalid ymd "${ymd}"`);
  }

  // Both bounds are tenant-local midnights resolved by datetimeLocalToUtcIso,
  // which measures the offset AT the target instant. Two defects are fixed by
  // doing it this way rather than by hand:
  //
  // 1. The previous implementation measured the offset at a guessed instant and
  //    claimed in its own comment to be "DST-safe because the offset is resolved
  //    at the actual instant". It was not. Measured, Australia/Sydney:
  //      2026-10-04 -> day started 2026-10-03 23:00  (the PREVIOUS day)
  //      2026-04-05 -> day started 2026-04-05 01:00  (an hour late)
  //
  // 2. `startMs + 86_400_000 - 1` assumes every day is 24 hours. The two DST
  //    days are 23 and 25 hours, so the end bound was an hour out on those days
  //    even when the start was right. Deriving the end from the NEXT day's local
  //    midnight makes the length follow the calendar instead of assuming it.
  //
  // Both reach daily aggregation windows across 74 call sites, including
  // Services Australia claim ranges — an hour of the wrong day in or out of a
  // billing window is a money defect.
  const startIso = datetimeLocalToUtcIso(`${ymd}T00:00:00`, tz);
  const nextDay = new Date(Date.UTC(y, m - 1, d) + 86_400_000);
  const nextYmd = `${String(nextDay.getUTCFullYear()).padStart(4, '0')}-${String(
    nextDay.getUTCMonth() + 1,
  ).padStart(2, '0')}-${String(nextDay.getUTCDate()).padStart(2, '0')}`;
  const nextStartIso = datetimeLocalToUtcIso(`${nextYmd}T00:00:00`, tz);
  if (!startIso || !nextStartIso) {
    throw new Error(`dayBoundsInTz: could not resolve local midnight for "${ymd}" in "${tz}"`);
  }
  return {
    startUtc: startIso,
    endUtc: new Date(Date.parse(nextStartIso) - 1).toISOString(),
  };
}

/**
 * Length of the local calendar day `ymd` in `tz`, in whole seconds.
 *
 * DST-safe: Hobart / Sydney spring-forward days are 23 hours (82_800s);
 * autumn fall-back days are 25 hours (90_000s). Hardcoding 24×60×60 makes
 * a full RN day permanently short on the short day and tolerates a one-hour
 * gap on the long day — false non-compliance and false compliance.
 *
 * Prefer this (or the matching SQL helper) over `24 * 60 * 60` anywhere a
 * "full day of coverage" threshold is needed.
 */
export function dayLengthSecondsInTz(ymd: string, tz: string = DEFAULT_TZ): number {
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) {
    throw new Error(`dayLengthSecondsInTz: invalid ymd "${ymd}"`);
  }
  const startIso = datetimeLocalToUtcIso(`${ymd}T00:00:00`, tz);
  const nextDay = new Date(Date.UTC(y, m - 1, d) + 86_400_000);
  const nextYmd = `${String(nextDay.getUTCFullYear()).padStart(4, '0')}-${String(
    nextDay.getUTCMonth() + 1,
  ).padStart(2, '0')}-${String(nextDay.getUTCDate()).padStart(2, '0')}`;
  const nextStartIso = datetimeLocalToUtcIso(`${nextYmd}T00:00:00`, tz);
  if (!startIso || !nextStartIso) {
    throw new Error(
      `dayLengthSecondsInTz: could not resolve local day length for "${ymd}" in "${tz}"`,
    );
  }
  return Math.round((Date.parse(nextStartIso) - Date.parse(startIso)) / 1000);
}

/**
 * Returns the start (inclusive) and end (exclusive) UTC instants for an
 * AIHW QI calendar quarter anchored in `tz`. Quarter is 1..4. Used to slice
 * SIRS / continence / ADL / hospitalisation rows for AIHW QI submission.
 *
 * Replaces the prior `Date.UTC(year, quarterIndex * 3, 1)` pattern that
 * misallocated 11 hours either side of the quarter boundary in tenants
 * outside UTC (every Australian tenant).
 */
export function quarterBoundsInTz(
  year: number,
  quarter: 1 | 2 | 3 | 4,
  tz: string = DEFAULT_TZ,
): { startUtc: string; endUtcExclusive: string; startYmd: string; endYmd: string; days: number } {
  const startMonth = (quarter - 1) * 3 + 1; // Q1->1, Q2->4, Q3->7, Q4->10
  const endMonth = startMonth + 3; // exclusive boundary; Q4 -> 13 means next year
  const endYear = endMonth > 12 ? year + 1 : year;
  const endMonthNorm = endMonth > 12 ? endMonth - 12 : endMonth;
  const startYmd = `${year}-${String(startMonth).padStart(2, '0')}-01`;
  const endYmd = `${endYear}-${String(endMonthNorm).padStart(2, '0')}-01`;
  const startBounds = dayBoundsInTz(startYmd, tz);
  const endBoundsExclusive = dayBoundsInTz(endYmd, tz);
  const startMs = Date.parse(startBounds.startUtc);
  const endMs = Date.parse(endBoundsExclusive.startUtc);
  const days = Math.max(1, Math.round((endMs - startMs) / 86_400_000));
  return {
    startUtc: startBounds.startUtc,
    endUtcExclusive: endBoundsExclusive.startUtc,
    startYmd,
    endYmd,
    days,
  };
}

/**
 * Converts an HTML `datetime-local` input string (no timezone, e.g.
 * `"2026-06-15T22:00"` or `"2026-06-15T22:00:30"`) into a UTC ISO instant
 * interpreted in the supplied IANA timezone.
 *
 * Codex P1-4 — `<input type="datetime-local">` produces a timezone-less
 * wall-clock string. Persisting it directly as `timestamptz` makes Postgres
 * interpret the value as UTC (or server-local-TZ), which means an NSW
 * coordinator entering "2026-06-15 22:00" can land in the DB as
 * `2026-06-15 12:00:00+00` (10h shift). That shifts:
 *   - the engine's public-holiday detection day,
 *   - rate-version-of-day resolution,
 *   - cumulative-hours OT week bucketing.
 *
 * DST-safe: uses the same offset-walk pattern as `dayBoundsInTz` — guesses
 * the UTC instant, measures the actual offset reported by Intl in the
 * tenant tz at that instant, then corrects. Works for arbitrary
 * `datetime-local` precision (HH:mm, HH:mm:ss, HH:mm:ss.sss).
 *
 * Returns null on malformed input — callers should treat null as "skip
 * this field" rather than fabricating a timestamp.
 *
 * Example (NSW, AEST = UTC+10):
 *   datetimeLocalToUtcIso('2026-06-15T22:00', 'Australia/Sydney')
 *     → '2026-06-15T12:00:00.000Z'
 */
export function datetimeLocalToUtcIso(
  datetimeLocal: string | null | undefined,
  tz: string = DEFAULT_TZ,
): string | null {
  if (!datetimeLocal) return null;
  // datetime-local accepts: YYYY-MM-DDTHH:mm | YYYY-MM-DDTHH:mm:ss | YYYY-MM-DDTHH:mm:ss.sss
  const match = datetimeLocal.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/,
  );
  if (!match) return null;
  const [, yStr, moStr, dStr, hStr, miStr, sStr, msStr] = match;
  const y = Number(yStr);
  const mo = Number(moStr);
  const d = Number(dStr);
  const h = Number(hStr);
  const mi = Number(miStr);
  const s = sStr ? Number(sStr) : 0;
  const ms = msStr ? Number(msStr.padEnd(3, '0')) : 0;

  // Initial guess: treat the input as if it were UTC. Then measure how
  // Intl renders this instant in the tenant tz, and shift by the offset.
  const utcGuessMs = Date.UTC(y, mo - 1, d, h, mi, s, ms);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(utcGuessMs));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  const observedUtcMs = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
    ms,
  );
  // utcGuessMs - observedUtcMs is the tz-vs-UTC offset (positive in AEST).
  // The corrected real UTC instant is the guess plus that offset.
  const correctedUtcMs = utcGuessMs + (utcGuessMs - observedUtcMs);

  // SECOND PASS — required, not defensive.
  //
  // The offset above is measured at the GUESSED instant (the local time read as
  // if it were UTC), which sits ~10-11 hours away from the target in Australia.
  // On the day BEFORE a DST transition the guess lands on the far side of the
  // boundary, so the offset applied is the wrong one and the result is an hour
  // out. Measured before this fix, Australia/Sydney:
  //
  //   2026-10-03T22:00 -> stored 21:00   (day before DST starts)
  //   2026-04-04T22:00 -> stored 23:00   (day before DST ends)
  //
  // That reaches shift create/edit, medication administration times, and worker
  // availability windows — 46 call sites — so an hour's drift two days a year is
  // a pay and clinical-timing defect, not a rounding curiosity.
  //
  // Re-measuring AT the corrected instant gives the offset actually in force
  // there. One extra pass is sufficient: the second measurement is at most a
  // transition's width from the target, and offsets are constant either side.
  const verifyParts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(correctedUtcMs));
  const v = (type: string) => Number(verifyParts.find((p) => p.type === type)?.value ?? 0);
  const renderedUtcMs = Date.UTC(
    v('year'),
    v('month') - 1,
    v('day'),
    v('hour'),
    v('minute'),
    v('second'),
    ms,
  );
  // Zero when the corrected instant already renders as the requested wall time.
  const residualMs = utcGuessMs - renderedUtcMs;
  if (residualMs === 0) return new Date(correctedUtcMs).toISOString();

  // Otherwise re-apply using the offset in force at the corrected instant.
  //
  // NONEXISTENT WALL TIMES — the hour a spring-forward transition skips — have
  // no correct answer, and the direction this settles in is ZONE-DEPENDENT.
  // Measured 2026-08-03:
  //   Australia/Sydney  2026-10-04T02:30 -> 03:30  (forward, past the gap)
  //   Europe/London     2026-03-29T01:30 -> 02:30  (forward)
  //   America/New_York  2026-03-08T02:30 -> 01:30  (BACKWARD, before the gap)
  //
  // Every tenant on this platform is in an Australian zone, where it resolves
  // forward, so this is documented rather than normalised — imposing a direction
  // is a design decision, not a bug fix, and does not belong in a change whose
  // job is the day-before-DST defect. Callers that must not accept a snapped
  // time should verify the round trip themselves; roster roll-forward does
  // exactly that and skips instead.
  return new Date(correctedUtcMs + residualMs).toISOString();
}

/**
 * Returns the Monday-of-the-week YMD (`YYYY-MM-DD`) for `date` anchored in
 * `tz`. Used for SCHADS week-bucketing (cl 20.2/20.6 weekly caps) where the
 * grouping key must correspond to the tenant's local calendar week, not the
 * runtime-local week.
 *
 * Example: a shift at `2026-04-19T01:00:00Z` (Sunday UTC) resolves to
 * `2026-04-13` when tz='Australia/Sydney' (AEDT Monday morning = the
 * preceding Monday in Sydney, which is 13 April).
 */
export function mondayYmdInTz(date: Date, tz: string = DEFAULT_TZ): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    weekday: 'short',
  }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  const year = Number(get('year'));
  const month = Number(get('month'));
  const day = Number(get('day'));
  const weekday = get('weekday');
  const weekdayIndex = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(weekday);
  // offsetToMonday: negative = back to Monday, 0 = already Monday.
  const offsetToMonday = weekdayIndex === 0 ? -6 : 1 - weekdayIndex;
  // Walk the calendar in UTC — DST-safe because we're working in whole
  // days anchored to the tenant-resolved Y/M/D, not adding/subtracting
  // milliseconds from a local Date.
  const utc = Date.UTC(year, month - 1, day) + offsetToMonday * 86_400_000;
  const mondayYear = new Date(utc).getUTCFullYear();
  const mondayMonth = String(new Date(utc).getUTCMonth() + 1).padStart(2, '0');
  const mondayDay = String(new Date(utc).getUTCDate()).padStart(2, '0');
  return `${mondayYear}-${mondayMonth}-${mondayDay}`;
}
